/**
 * 门户展示态：句末 hardBreak 拆成独立段落（与 CMS preserveParagraphGap 同源）。
 * 一段里「句子。」+ br + 下句 → 两个 p，才能吃到 CSS 默认段后 1em。
 */
import { parseHtmlFragment } from '@/components/shared/utils/promoteInlineStyles'

const WS_RE = /^[\s\u00a0\u3000]*$/
/** 句末：。！？；及西文，允许尾随右引号/括号 */
const SENTENCE_END_RE = /[。！？；.!?;][》」』"'”’)\]）]*\s*$/

function cloneInlineNode(node) {
  if (!node || typeof node !== 'object') return node
  const next = { ...node }
  if (node.attrs) next.attrs = { ...node.attrs }
  if (Array.isArray(node.marks)) {
    next.marks = node.marks.map((mk) => ({
      ...mk,
      ...(mk.attrs ? { attrs: { ...mk.attrs } } : {}),
    }))
  }
  if (Array.isArray(node.content)) {
    next.content = node.content.map((c) => cloneInlineNode(c))
  }
  return next
}

function linePlainText(nodes) {
  return (nodes || [])
    .filter((n) => n?.type === 'text')
    .map((n) => String(n.text || ''))
    .join('')
    .replace(/[\s\u00a0\u3000]+$/g, '')
}

function lineHasSubstantial(nodes) {
  return (nodes || []).some((n) => {
    if (!n || n.type === 'hardBreak') return false
    if (n.type === 'text') return !WS_RE.test(n.text || '')
    return true
  })
}

function lineEndsWithSentence(nodes) {
  const plain = linePlainText(nodes)
  return !!plain && SENTENCE_END_RE.test(plain)
}

function splitInlineContentIntoLines(content) {
  const lines = [[]]
  const pushText = (node, text) => {
    if (!text) return
    const next = { type: 'text', text }
    if (node.marks) {
      next.marks = node.marks.map((mk) => ({
        ...mk,
        ...(mk.attrs ? { attrs: { ...mk.attrs } } : {}),
      }))
    }
    lines[lines.length - 1].push(next)
  }
  ;(content || []).forEach((node) => {
    if (!node || typeof node !== 'object') return
    if (node.type === 'hardBreak') {
      lines.push([])
      return
    }
    if (node.type === 'text') {
      const raw = String(node.text || '')
      if (!raw.includes('\n')) {
        lines[lines.length - 1].push(cloneInlineNode(node))
        return
      }
      raw.split('\n').forEach((part, idx) => {
        if (idx > 0) lines.push([])
        pushText(node, part)
      })
      return
    }
    lines[lines.length - 1].push(cloneInlineNode(node))
  })
  return lines
}

function linesToInlineContent(lines) {
  const content = []
  lines.forEach((line, idx) => {
    if (idx > 0) content.push({ type: 'hardBreak' })
    ;(line || []).forEach((n) => content.push(cloneInlineNode(n)))
  })
  return content
}

function hasLaterSubstantialLine(lines, fromIndex) {
  for (let i = fromIndex; i < lines.length; i += 1) {
    if (lineHasSubstantial(lines[i])) return true
  }
  return false
}

function splitSentenceHardBreaksInParagraph(paragraph) {
  if (!paragraph || paragraph.type !== 'paragraph') return null
  const content = paragraph.content
  if (!Array.isArray(content) || !content.some((n) => n?.type === 'hardBreak' || (n?.type === 'text' && String(n.text || '').includes('\n')))) {
    return null
  }

  const lines = splitInlineContentIntoLines(content)
  if (lines.length < 2) return null

  const groups = []
  let current = []

  const flush = () => {
    if (!current.length) return
    const substantialLines = current.filter(lineHasSubstantial)
    if (!substantialLines.length) {
      current = []
      return
    }
    groups.push(substantialLines)
    current = []
  }

  for (let i = 0; i < lines.length; i += 1) {
    const line = lines[i]
    if (!lineHasSubstantial(line)) {
      if (
        current.length
        && lineEndsWithSentence(current[current.length - 1])
        && hasLaterSubstantialLine(lines, i + 1)
      ) {
        flush()
      }
      continue
    }

    if (current.length && lineEndsWithSentence(current[current.length - 1])) {
      flush()
    }
    current.push(line)
  }
  flush()

  if (groups.length < 2) return null

  return groups.map((groupLines) => {
    const inline = linesToInlineContent(groupLines)
    return {
      type: 'paragraph',
      ...(paragraph.attrs ? { attrs: { ...paragraph.attrs } } : {}),
      ...(inline.length ? { content: inline } : {}),
    }
  })
}

/** 就地修改 JSON：把句末 hardBreak 拆成独立段落 */
export function splitSentenceHardBreaksInJson(doc) {
  if (!doc || typeof doc !== 'object') return false
  let changed = false

  const expandList = (nodes) => {
    if (!Array.isArray(nodes)) return nodes
    const out = []
    for (const node of nodes) {
      if (!node || typeof node !== 'object') {
        out.push(node)
        continue
      }
      if (Array.isArray(node.content)) {
        node.content = expandList(node.content)
      }
      if (node.type === 'paragraph') {
        const parts = splitSentenceHardBreaksInParagraph(node)
        if (parts && parts.length >= 2) {
          out.push(...parts)
          changed = true
          continue
        }
      }
      out.push(node)
    }
    return out
  }

  if (Array.isArray(doc.content)) {
    doc.content = expandList(doc.content)
  }
  return changed
}

/** HTML：对每个 p 内句末后的 br 拆成多个 p（表格单元格不拆） */
export function splitSentenceHardBreaksInHtml(html) {
  if (!html || !/<br\b/i.test(html)) return html
  try {
    const parsed = parseHtmlFragment(html, '__hb_split')
    if (!parsed?.root) return html
    const { doc, root } = parsed
    let changed = false

    Array.from(root.querySelectorAll('p')).forEach((p) => {
      if (p.closest('table')) return
      if (!p.querySelector('br')) return

      const lines = [[]]
      const pushText = (text) => {
        if (!text) return
        lines[lines.length - 1].push({ type: 'text', text })
      }
      const walk = (node) => {
        if (!node) return
        if (node.nodeType === 3) {
          const raw = String(node.nodeValue || '')
          raw.split('\n').forEach((part, idx) => {
            if (idx > 0) lines.push([])
            pushText(part)
          })
          return
        }
        if (node.nodeType !== 1) return
        if (node.tagName === 'BR') {
          lines.push([])
          return
        }
        if (node.childNodes.length === 0) {
          lines[lines.length - 1].push({ type: 'html', html: node.outerHTML })
          return
        }
        Array.from(node.childNodes).forEach(walk)
      }
      Array.from(p.childNodes).forEach(walk)

      const hasComplex = lines.some((line) => line.some((n) => n.type === 'html'))
      if (hasComplex) {
        const htmlLines = []
        let buf = ''
        const flushBuf = () => {
          htmlLines.push(buf)
          buf = ''
        }
        Array.from(p.childNodes).forEach((child) => {
          if (child.nodeType === 1 && child.tagName === 'BR') flushBuf()
          else if (child.nodeType === 3) buf += child.nodeValue || ''
          else if (child.nodeType === 1) buf += child.outerHTML
        })
        flushBuf()

        const groups = []
        let current = []
        const plainOf = (h) => String(h || '').replace(/<[^>]+>/g, '').replace(/[\s\u00a0\u3000]+$/g, '')
        const substantial = (h) => !!plainOf(h).replace(/[\s\u00a0\u3000]/g, '')
        const ends = (h) => SENTENCE_END_RE.test(plainOf(h))

        for (let i = 0; i < htmlLines.length; i += 1) {
          const line = htmlLines[i]
          if (!substantial(line)) {
            if (
              current.length
              && ends(current[current.length - 1])
              && htmlLines.slice(i + 1).some(substantial)
            ) {
              groups.push(current)
              current = []
            }
            continue
          }
          if (current.length && ends(current[current.length - 1])) {
            groups.push(current)
            current = []
          }
          current.push(line)
        }
        if (current.length) groups.push(current)
        if (groups.length < 2) return

        const frag = doc.createDocumentFragment()
        groups.forEach((group) => {
          const np = p.cloneNode(false)
          np.innerHTML = group.join('<br>')
          frag.appendChild(np)
        })
        p.replaceWith(frag)
        changed = true
        return
      }

      const textLines = lines.map((line) => line.map((n) => n.text || '').join(''))
      const groups = []
      let current = []
      const substantial = (t) => !!String(t || '').replace(/[\s\u00a0\u3000]/g, '')
      const ends = (t) => SENTENCE_END_RE.test(String(t || '').replace(/[\s\u00a0\u3000]+$/g, ''))

      for (let i = 0; i < textLines.length; i += 1) {
        const line = textLines[i]
        if (!substantial(line)) {
          if (
            current.length
            && ends(current[current.length - 1])
            && textLines.slice(i + 1).some(substantial)
          ) {
            groups.push(current)
            current = []
          }
          continue
        }
        if (current.length && ends(current[current.length - 1])) {
          groups.push(current)
          current = []
        }
        current.push(line)
      }
      if (current.length) groups.push(current)
      if (groups.length < 2) return

      const frag = doc.createDocumentFragment()
      groups.forEach((group) => {
        const np = p.cloneNode(false)
        group.forEach((text, idx) => {
          if (idx > 0) np.appendChild(doc.createElement('br'))
          np.appendChild(doc.createTextNode(text))
        })
        frag.appendChild(np)
      })
      p.replaceWith(frag)
      changed = true
    })

    return changed ? root.innerHTML : html
  } catch {
    return html
  }
}
